import * as React from 'react';
import * as _ from 'lodash';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Notification,
  Logging,
  renderEdit,
  API,
  Product,
  NotificationData,
  ResponseError,
  UserInfo,
  UserContext,
  ContentLoading,
  renderCheckbox,
  ExemptionType,
  Footer,
  renderValidationMessage,
  VertexLink,
  validateIllegalCharachters,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import ProductItemProps from './ProductProps.interface';
import '../common-ecwizard.scss';
import './product.scss';
import useProduct from './useProduct';
import useExemptionTypes from '../ExemptionTypes/useExemptionTypes';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const illegalProductNameCharacters: string[] = ['|'];

const controlValidation: ControlValidation = {
  requiredFields: ['product-name'],
  minMaxSize: [
    { field: 'product-name', maxSize: 60 },
    { field: 'taxability-driver-code', maxSize: 60 },
  ],
  illegalCharacters: [
    { field: 'product-name', illegalCharacters: illegalProductNameCharacters },
  ],
  customMessages: [],
};

interface ParamTypes {
  uuid: string,
}

const ProductItem = ({
  gatewayEndPoint,
}: ProductItemProps): JSX.Element => {
  // Get the Uuid from the Url Parameters
  const { uuid } = useParams<ParamTypes>();
  const [isLoaded, setIsLoaded] = React.useState(uuid === undefined);

  // Load the Exemption Types - It will be immediately availabe as it was loaded by Parent
  const [exemptionTypes, exemptionTypesNotification] = useExemptionTypes(gatewayEndPoint, false);

  // Load the Product if the UUID is specified during an Edit
  const [product, productNotification] = uuid ? useProduct(gatewayEndPoint, uuid) : [null, null];

  // User Selected Exemption Values
  const [userExemptionTypes, setUserExemptionTypes] = React.useState(null);

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);
  const [redirect, setRedirect] = React.useState(null);

  const [productName, setProductName] = React.useState(null);
  const [taxabilityDriverCode, setTaxabilityDriverCode] = React.useState(null);
  const [productClassInd, setProductClassInd] = React.useState(null);

  // Get the Visible Exemption Types
  const getVisibleExemptionTypes = (exemptionTypeArray: ExemptionType[]): ExemptionType[] => { // eslint-disable-line arrow-body-style
    return exemptionTypeArray ? exemptionTypeArray.filter((x: ExemptionType) => x.isUsedByClient === true) : null;
  };

  // Make the API Call to Add the Product
  const addProduct = (newProduct: Product) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Product
      await api.ecwizard.addProductV41(newProduct);

      // Direct back to Products
      setRedirect('/ecwizard/products');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Product Save Error',
        description: `Unable to Add the Product '${newProduct.name}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Edit the Product
  const editProduct = (updatedProduct: Product) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Edit the Product
      await api.ecwizard.editProductV41(updatedProduct);

      // Direct back to Products
      setRedirect('/ecwizard/products');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Product Save Error',
        description: `Unable to Edit the Product '${updatedProduct.name}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Save Button was Pressed
  const onSave = (): void => {
    if (exemptionTypes) {
      const exemptionTypeIds: number [] = [];

      let index = 0;

      // Get the IDs of the Selected Exemption Types
      getVisibleExemptionTypes(exemptionTypes).forEach((exemptionType: ExemptionType) => {
        // Add the Exception Type if selected
        if (userExemptionTypes[index] === true) {
          exemptionTypeIds.push(exemptionType.id);
        }
        index += 1;
      });

      const addEditProduct: Product = {
        id: null,
        uuid,
        name: productName,
        taxabilityDriverCode,
        productClassInd,
        exemptionTypeIds,
      };

      if (uuid) {
        Logging.Info('Edit Product', addEditProduct);

        dispatch(editProduct(addEditProduct));
      } else {
        Logging.Info('Add Product', addEditProduct);

        dispatch(addProduct(addEditProduct));
      }
    }
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const validateValues = (): boolean => {
    if (!productName) {
      return false;
    }

    if (productName?.length > 60) {
      return false;
    }

    if (taxabilityDriverCode?.length > 60) {
      return false;
    }

    if (!validateIllegalCharachters(productName, illegalProductNameCharacters)) {
      return false;
    }

    // One Exemption Type must at least be selected
    if ((userExemptionTypes === null) || (userExemptionTypes.filter((x: boolean) => x === true).length === 0)) {
      return false;
    }

    return true;
  };

  // Render the Exemption Types
  const renderExemptionTypes = (): JSX.Element | JSX.Element[] => {
    if (exemptionTypes && userExemptionTypes) {
      let index = -1;

      if (getVisibleExemptionTypes(exemptionTypes).length === 0) {
        return (
          <>No Exemption Types configured</>
        );
      }

      return getVisibleExemptionTypes(exemptionTypes).map((exemptionType: ExemptionType) => {
        index += 1;
        return (
          <div className="field33 exemptionTypeClass" key={`exemption-type-${exemptionType.id}`}>
            {renderCheckbox(`exemption-type-${exemptionType.id}`, exemptionType.name, userExemptionTypes[index], index,
              setUserExemptionTypes, controlStateEditable)}
          </div>
        );
      });
    }

    return (
      <></>
    );
  };

  // Select All Exemption Types
  const onSelectAll = (): void => {
    if (exemptionTypes) {
      setUserExemptionTypes(Array(getVisibleExemptionTypes(exemptionTypes).length).fill(true));
    }
  };

  // Clear All Exemption Types
  const onClearAll = (): void => {
    if (exemptionTypes) {
      setUserExemptionTypes(Array(getVisibleExemptionTypes(exemptionTypes).length).fill(false));
    }
  };

  // Exemption Types were loaded during an Add or Edit
  React.useEffect(() => {
    // Exemption Types already loaded
    if (exemptionTypes) {
      Logging.Info('Exemption Types:', exemptionTypes);

      // Edit
      if (uuid) {
        // Product already Loaded - If Product is not yet loaded this will be done later
        if (product) {
          const values: boolean[] = [];

          // Get the Checkbox States for only the visible Exemption Types
          getVisibleExemptionTypes(exemptionTypes).forEach((exemptionType: ExemptionType) => {
            const value = product.exemptionTypeIds?.includes(exemptionType.id);
            values.push(value);
          });

          setUserExemptionTypes(values);
        }
      // Add
      } else {
        // Select all the Options
        setUserExemptionTypes(Array(getVisibleExemptionTypes(exemptionTypes)?.length).fill(true));
      }
    }
  }, [exemptionTypes]);

  // Product was loaded during an Edit
  React.useEffect(() => {
    // Product was loaded
    if (product) {
      setProductName(product.name);
      setTaxabilityDriverCode(product.taxabilityDriverCode);
      setProductClassInd(product.productClassInd);

      // Exemption Types already loaded - If Exception Types is not yet loaded, this will be done later
      if (exemptionTypes) {
        const values: boolean[] = [];

        // Get the Checkbox States for only the visible Exemption Types
        getVisibleExemptionTypes(exemptionTypes).forEach((exemptionType: ExemptionType) => {
          const value = product.exemptionTypeIds?.includes(exemptionType.id);
          values.push(value);
        });

        setUserExemptionTypes(values);
      }

      setIsLoaded(true);
    }
  }, [product]);

  // Unable to Load the Product during an Edit
  React.useEffect(() => {
    if (productNotification || exemptionTypesNotification) {
      setNotification(productNotification || exemptionTypesNotification);
    }
  }, [productNotification, exemptionTypesNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Product: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setRedirect(null);
    };
  }, []);

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  const canSave: boolean = validateValues();
  const validationMessage = userExemptionTypes?.filter((x: boolean) => x === true).length === 0
    ? 'At least one Exemption Type must be selected.' : null;

  return (
    <>
      {
        !isLoaded
          ? (
            !productNotification
            && (
              <ContentLoading loadingMessage="Loading Product ..." />
            )
          )
          : (
            <div className="ecwizardContainer">
              <div className="ecwizardHeaderBack">
                <VertexLink href="#/ecwizard/products" display="Back to Products" />
              </div>
              <div className="ecwizardHeader">
                <div className="ecwizardHeaderLeft">
                  {uuid ? 'Edit Product' : 'Add Product'}
                </div>
              </div>
              <div className="ecwizardHeaderDescription">
                Add all the products from this seller that you would like to enable for buyer tax exemption.
              </div>
              <hr />
              <div className="ecwizardContentControls">
                {renderEdit(33, 'product-name', 'Product Name*', 'text', 'Product Name',
                  productName, setProductName, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'taxability-driver-code', 'Taxability Driver Code', 'text', 'Taxability Driver Code',
                  taxabilityDriverCode, setTaxabilityDriverCode, controlStateEditable, controlStyle, controlValidation)}
                <div className="field33 productClass">
                  {renderCheckbox('is-product-class', 'Product Class', productClassInd, null, setProductClassInd, controlStateEditable)}
                </div>
                <div className="ecwizardHeaderSmall">
                  Exemption Types*
                </div>
                <div className="ecwizardContentButtons">
                  <div className="ecwizardContentButtonsLeft">
                    <div className="buttonItemBase">
                      <VtxButton
                        id="selectAllButton"
                        icon={<VtxIcon type="check-square" />}
                        onClick={onSelectAll}
                        type="default"
                      >
                        Select All
                      </VtxButton>
                    </div>
                    <div className="buttonItemBase">
                      <VtxButton
                        id="clearAllButton"
                        icon={<VtxIcon type="border" />}
                        onClick={onClearAll}
                        type="default"
                      >
                        Clear All
                      </VtxButton>
                    </div>
                  </div>
                </div>
                <div className="ecwizardContentControlsBorder">
                  {renderExemptionTypes()}
                </div>
                {renderValidationMessage('exemption-types', validationMessage)}
              </div>
              <Footer cancelLink="#/ecwizard/products" saveEnabled={canSave} onSave={onSave} />
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default ProductItem;
